import { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    DepartmentVariableSettings,
    getDepartmentVariableSettings,
} from '../../services/api/departmentVariablesService';
import { getUsersByDepartment } from '../../services/apiService';
import { DepartmentUser, hasStartedSetup } from './utils/settingsView';
import SetupWizard from './setup-wizard/SetupWizard';
import SettingsOverview from './SettingsOverview';
import LoadingRing from '../ui-components/LoadingRing';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { EVENT_KEYS, trackLoad } from '../../services/api/trackingService';

type Mode = 'loading' | 'wizard' | 'overview';

export default function DepartmentSettingsPage() {
    const [settings, setSettings] = useState<DepartmentVariableSettings | null>(
        null
    );
    const [users, setUsers] = useState<DepartmentUser[]>([]);
    const [mode, setMode] = useState<Mode>('loading');
    const { toast } = useToast();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const refetchSettings = async () => {
        try {
            setSettings(await getDepartmentVariableSettings());
        } catch {
            toast({
                title: t('departmentVariables.errors.failedToLoad'),
                variant: 'destructive',
            });
        }
    };

    useEffect(() => {
        trackLoad(EVENT_KEYS.viewDepartmentVariables, {});
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            const [settingsResult, usersResult] = await Promise.allSettled([
                getDepartmentVariableSettings(),
                getUsersByDepartment(),
            ]);
            setUsers(
                usersResult.status === 'fulfilled' &&
                    Array.isArray(usersResult.value)
                    ? usersResult.value
                    : []
            );
            if (
                settingsResult.status === 'rejected' ||
                usersResult.status === 'rejected'
            ) {
                toast({
                    title: t('departmentVariables.errors.failedToLoad'),
                    variant: 'destructive',
                });
            }
            if (settingsResult.status === 'fulfilled') {
                setSettings(settingsResult.value);
                setMode(
                    hasStartedSetup(settingsResult.value)
                        ? 'overview'
                        : 'wizard'
                );
                return;
            }
            setMode('overview');
        };
        void fetchData();
    }, [toast]);

    if (mode === 'loading') {
        return (
            <div className="flex justify-center items-center min-h-[60vh]">
                <LoadingRing size="large" />
            </div>
        );
    }

    return (
        <div className="container mx-auto max-w-3xl px-4 pt-8 pb-28">
            {/* Page Header */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold">{t('departmentVariables.title')}</h1>
                    {/* Back link */}
                    <Button
                        variant="outline"
                        onClick={() => navigate('/department-policies')}
                        size="sm"
                        className="gap-1.5"
                    >
                        <ChevronLeft className="h-4 w-4" />
                        {t('departmentVariables.backToPolicies')}
                    </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                    {t('departmentVariables.subtitle')}
                </p>
            </div>

            {!settings ? (
                <p className="text-sm text-subtle-foreground font-medium">
                    {t('departmentVariables.errors.failedToLoad')}
                </p>
            ) : mode === 'wizard' ? (
                <SetupWizard
                    settings={settings}
                    users={users}
                    onSaved={refetchSettings}
                    onComplete={() => setMode('overview')}
                />
            ) : (
                <SettingsOverview
                    settings={settings}
                    users={users}
                    onSaved={refetchSettings}
                />
            )}
        </div>
    );
}
